import React, { useState } from 'react';
import { ArrowRight, Home, Users, TrendingUp, Heart, CheckCircle } from 'lucide-react';
import { HamburgerIcon } from '../components/HamburgerIcon';
import { MobileMenu } from '../components/MobileMenu';
import { DemoModal } from '../components/DemoModal';
import nfImage from '../images/nf.jpg';
import nfImage7 from '../images/nf7.jpg';
import nfImage3 from '../images/nf3.jpg';
import nfHero from '../images/nfhero.jpg';
import scrollLogo from '../images/scroll-logo.png';
import bigLogo from '../images/big-logo.png';

const LandingPageA = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll event
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLinkClick = (e) => {
    // Only intercept if not hamburger or modal elements
    if (
      !e.target.closest('#mobile-menu-button') &&
      !e.target.closest('.demo-modal-content') &&
      !e.target.id.includes('hamburger')
    ) {
      e.preventDefault();
      setModalOpen(true);
    }
  };

  const programs = [
    {
      icon: Home,
      title: 'Housing Development',
      description: 'Quality housing solutions that strengthen community foundations and provide safe, sustainable living spaces.',
    },
    {
      icon: TrendingUp,
      title: 'Economic Empowerment',
      description: 'Programs designed to boost local economy through intelligent resource management and community support.',
    },
    {
      icon: Users,
      title: 'Community Support',
      description: 'Comprehensive services meeting human needs while fostering vibrant, thriving neighborhoods.',
    },
  ];

  const stats = [
    { value: '500+', label: 'Families Served' },
    { value: '15+', label: 'Years of Impact' },
    { value: '50+', label: 'Community Programs' },
  ];

  return (
    <div className="min-h-screen bg-white" onClick={handleLinkClick}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm transition-all duration-300 ${
        scrolled ? 'shadow-lg' : 'shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'h-16' : 'h-20'
          }`}>
            <img
              src={scrolled 
                ? scrollLogo
                : bigLogo
              }
              alt="Northfield Community LDC"
              className={`transition-all duration-300 w-auto ${
                scrolled ? 'h-10' : 'h-16'
              }`}
            />
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#about" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                About
              </a>
              <a href="#programs" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Programs
              </a>
              <a href="#impact" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Impact
              </a>
              <a href="#contact" className="text-[#231F20] hover:text-[#5FC5B8] font-medium transition-colors duration-200">
                Contact
              </a>
              <button className="px-6 py-2.5 bg-[#5FC5B8] hover:bg-[#4db5a7] text-white font-semibold rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                Get Involved
              </button>
            </nav>

            <div className="md:hidden text-[#231F20]">
              <HamburgerIcon
                isOpen={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              />
            </div>
          </div>
        </div>
      </header>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-playfair text-5xl lg:text-6xl font-bold text-[#231F20] leading-tight mb-6">
                Building Thriving Communities Together
              </h1>
              <p className="font-source text-lg text-gray-600 leading-relaxed mb-8">
                Northfield Community LDC believes that the strength of a community lies in the quality of its housing, the vibrancy of its local economy, and its provision for human needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#231F20] hover:bg-[#231F20]/90 text-white font-semibold rounded-full transition-all duration-200 hover:-translate-y-1 hover:shadow-xl">
                  Learn More
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#5FC5B8] text-[#5FC5B8] hover:bg-[#5FC5B8] hover:text-white font-semibold rounded-full transition-all duration-200 hover:-translate-y-1">
                  Contact Us
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={nfImage7}
                  alt="Northfield Community LDC - Community gathering"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#231F20]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-playfair text-5xl font-bold text-[#5FC5B8] mb-2">
                  {stat.value}
                </div>
                <div className="font-source text-white text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="about" className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#231F20] mb-6">
            Our Mission
          </h2>
          <p className="font-source text-xl text-gray-600 leading-relaxed mb-8">
            We strive to promote community strength directly and with the support of other resources in the community. Northfield Community LDC achieves these goals through the intelligent use of energy, protection of the environment, and economic empowerment.
          </p>
        </div>
      </section>

      {/* Community Impact Image Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src={nfImage3}
                alt="Northfield Community programs"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src={nfHero}
                alt="Community support and development"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#231F20] mb-4">
              Our Programs
            </h2>
            <p className="font-source text-xl text-gray-600">
              Comprehensive solutions for community development
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#5FC5B8]/10 rounded-xl flex items-center justify-center mb-6">
                  <program.icon className="w-7 h-7 text-[#5FC5B8]" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-[#231F20] mb-4">
                  {program.title}
                </h3>
                <p className="font-source text-gray-600 leading-relaxed">
                  {program.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-involved" className="py-20 px-6 lg:px-8 bg-gradient-to-br from-[#5FC5B8] to-[#4db5a7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-white mb-6">
            Join Us in Making a Difference
          </h2>
          <p className="font-source text-xl text-white/90 leading-relaxed mb-8">
            Together, we can build stronger, more vibrant communities that serve everyone.
          </p>
          <button className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white hover:bg-gray-50 text-[#231F20] font-bold rounded-full transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl">
            Get Involved Today
            <Heart className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Accessibility Section */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-playfair text-2xl font-bold text-[#231F20] mb-6 text-center">
            Accessibility & Services
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-600">
                Reserved parking spaces and wheelchair accessible ramp
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-600">
                Bi-lingual counselors available on staff
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-600">
                Video Relay Service (VRS) for deaf, hard of hearing, or speech-impaired
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-[#5FC5B8] flex-shrink-0 mt-1" />
              <p className="font-source text-gray-600">
                Full accessibility accommodations available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#231F20] text-white py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="inline-block bg-white px-4 py-2 rounded-lg mb-4">
                <img
                  src={bigLogo}
                  alt="Northfield Community LDC"
                  className="h-12 w-auto"
                />
              </div>
              <p className="font-source text-gray-300 text-sm">
                Building thriving communities together
              </p>
            </div>
            <div>
              <h4 className="font-playfair text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 font-source">
                <li>
                  <a href="#about" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#programs" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200">
                    Programs
                  </a>
                </li>
                <li>
                  <a href="#impact" className="text-gray-300 hover:text-[#5FC5B8] transition-colors duration-200">
                    Impact
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-playfair text-lg font-bold mb-4">Contact</h4>
              <p className="font-source text-gray-300 text-sm mb-2">
                Phone: 212-NEW-YORK (212-639-9675)
              </p>
              <p className="font-source text-gray-300 text-sm">
                Accessible services: 212-504-4115
              </p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="font-source text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} Northfield Community LDC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <DemoModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default LandingPageA;